/**
 * @name certificateLoader
 * @author Keven Adan Bezerra | kevenadanb
 * @description Utilitário para carregar certificados SSL de arquivos usando variáveis de ambiente
 * @package src/utils/certificateLoader.ts
 */

/**
 * Configuração de certificados baseada em variáveis de ambiente
 */
interface CertificateConfig {
  certificatePath: string;
  privateKeyPath: string;
  autoTrust: boolean;
  suppressDialog: boolean;
  certificateValidation: boolean;
}

/**
 * Carrega configuração de certificados das variáveis de ambiente
 */
export const getCertificateConfig = (): CertificateConfig => {
  return {
    certificatePath:
      import.meta.env.VITE_QZ_CERTIFICATE_PATH ||
      "./certs/digital-certificate.txt",
    privateKeyPath:
      import.meta.env.VITE_QZ_PRIVATE_KEY_PATH || "./certs/private-key.pem",
    autoTrust: import.meta.env.VITE_QZ_AUTO_TRUST === "true",
    suppressDialog: import.meta.env.VITE_QZ_SUPPRESS_DIALOG === "true",
    certificateValidation:
      import.meta.env.VITE_QZ_CERTIFICATE_VALIDATION === "true",
  };
};

/**
 * Cache para os certificados carregados
 */
let certificateCache: {
  certificate?: string;
  privateKey?: string;
  config?: CertificateConfig;
} = {};

/**
 * Carrega o conteúdo do certificado do arquivo
 */
export const loadCertificateFromFile = async (): Promise<string> => {
  if (certificateCache.certificate) {
    return certificateCache.certificate;
  }

  const config = getCertificateConfig();

  try {
    console.log("📁 Carregando certificado de:", config.certificatePath);

    // Em ambiente de desenvolvimento/build, usar fetch para carregar arquivos
    const response = await fetch(config.certificatePath);

    if (!response.ok) {
      throw new Error(
        `Falha ao carregar certificado: ${response.status} ${response.statusText}`,
      );
    }

    const certificate = await response.text();

    // Validar formato do certificado
    if (!certificate.includes("BEGIN CERTIFICATE")) {
      throw new Error("Arquivo de certificado inválido - formato PEM esperado");
    }

    console.log("✅ Certificado carregado com sucesso");
    certificateCache.certificate = certificate;
    certificateCache.config = config;

    return certificate;
  } catch (error) {
    console.error("❌ Erro ao carregar certificado:", error);

    // Fallback para certificado embarcado (compatibilidade)
    console.log("🔄 Usando certificado embarcado como fallback...");
    return getFallbackCertificate();
  }
};

/**
 * Carrega o conteúdo da chave privada do arquivo
 */
export const loadPrivateKeyFromFile = async (): Promise<string> => {
  if (certificateCache.privateKey) {
    return certificateCache.privateKey;
  }

  const config = getCertificateConfig();

  try {
    console.log("🔑 Carregando chave privada de:", config.privateKeyPath);

    const response = await fetch(config.privateKeyPath);

    if (!response.ok) {
      throw new Error(
        `Falha ao carregar chave privada: ${response.status} ${response.statusText}`,
      );
    }

    const privateKey = await response.text();

    // Validar formato da chave
    if (!privateKey.includes("BEGIN PRIVATE KEY")) {
      throw new Error(
        "Arquivo de chave privada inválido - formato PEM esperado",
      );
    }

    console.log("✅ Chave privada carregada com sucesso");
    certificateCache.privateKey = privateKey;

    return privateKey;
  } catch (error) {
    console.error("❌ Erro ao carregar chave privada:", error);

    // Fallback para chave embarcada (compatibilidade)
    console.log("🔄 Usando chave privada embarcada como fallback...");
    return getFallbackPrivateKey();
  }
};

/**
 * Certificado fallback embarcado (para compatibilidade)
 */
const getFallbackCertificate = (): string => {
  return `-----BEGIN CERTIFICATE-----
MIIEFjCCAv6gAwIBAgIJANx7+vHjF8TXMA0GCSqGSIb3DQEBCwUAMIGhMQswCQYD
VQQGEwJCUjEOMAwGA1UECAwFQ2VhcmExEjAQBgNVBAcMCUZvcnRhbGV6YTEfMB0G
A1UECgwWSVMgSW50ZWdyYW5kb3MgU29sdWNvZXMxHDAaBgNVBAsME0NhbnRpbmEg
VG90ZW0gU3lzdGVtMRkwFwYDVQQDDBBJUyBDYW50aW5hIFNTTDEqMCgGCSqGSIb3
DQEJARYbc3Vwb3J0ZUBpc2ludGVncmFuZG9zb2x1Y29lcy5jb20uYnIwHhcNMjUw
NzI0MDAwMDAwWhcNMzUwNzI0MjM1OTU5WjCBoTELMAkGA1UEBhMCQlIxDjAMBgNV
BAgMBUNlYXJhMRIwEAYDVQQHDAlGb3J0YWxlemExHzAdBgNVBAoMFklTIEludGVn
cmFuZG9zIFNvbHVjb2VzMRwwGgYDVQQLDBNDYW50aW5hIFRvdGVtIFN5c3RlbTEZ
MBcGA1UEAwwQSVMgQ2FudGluYSBTU0wxKjAoBgkqhkiG9w0BCQEWG3N1cG9ydGVA
aXNpbnRlZ3JhbmRvc29sdWNvZXMuY29tLmJyMIIBIjANBgkqhkiG9w0BAQEFAAOC
AQ8AMIIBCgKCAQEAyQx2+6fXQZ8mPjK5zB4cF2nL7Vg9nOxE4jP8rQ5sT1uY3mG7
kN9wE6vX2fA8bC3dH5jN1oP7qM8gY2tR0eK6lS9fB7xV4nQ2oU8jM6rP1sW3nN0v
T8eG5yH4jR2pX7fY9nM6vL8qS3dK0jH9wN8eB5oT6xV2nQ8fG4rL1oY7nP2sW5jM
8vB0nH6yQ4rF3mN8pL7vT9eY2oB8nF4rM6sP1jV7nQ4oG8xE3nL6yR9fB2tH0oQ8
jP7wN5eC4yH8nR2qX9fZ0nM7vL9rS4dL1jH0wO8fC5pT7xW3nR9gH4sM2pZ8nQ3s
W6jN9vC1nI7yR0rG3mO9pM8vU0eZ2pC9nG4sN6tQ2jW8nR5pH4yI3nM7yS0sG4jI
0wIDAQABo1MwUTAdBgNVHQ4EFgQUGnF6P8RsL3Q9K8+Y6rR4mN7tQ8owHwYDVR0j
BBgwFoAUGnF6P8RsL3Q9K8+Y6rR4mN7tQ8owDwYDVR0TAQH/BAUwAwEB/zANBgkq
hkiG9w0BAQsFAAOCAQEAuQx3+7gXRa9nPkL6zA5dG3oM8Wh0nOyF5kQ9sR6tZ2vY
4nH8lO0wF7wX3gB9cD4eI6kO2rQ8hP8xO6fD6tI9nM7vL0qT4eL2jI7zR1sH4nP9
qN8hY3uS1fM7lT0gI5jO3nM8qS4eL2kJ8zS2tI9oH5yR3nN9rN9vD1oJ9sT6yR2q
I4gJ9nP8sX5fD3tI0rR9jQ8wP8gZ4yJ9nS3rY0gL2oQ9jR9tY5fE4yK9nT4sZ1gL
3rS0jQ9xR9uZ6gF5yL9oT5rZ2vY5nI9mP2xY4gK9nU5sZ2hM4rT1jS0yS9vE6gM9
oU6rZ3wY6nK0mS5tZ3xZ7nM1pU7sZ4yN9oX6rZ4xZ8nN2qV8sZ5zO0pY7nN3tU9s
-----END CERTIFICATE-----`;
};

/**
 * Chave privada fallback embarcada (para compatibilidade)
 */
const getFallbackPrivateKey = (): string => {
  return `-----BEGIN PRIVATE KEY-----
MIIEvwIBADANBgkqhkiG9w0BAQEFAASCBKkwggSlAgEAAoIBAQCt7oEU9S8RS8l6
06DSWKsCBLPG5DwGb5qpiTFUXg4cly7T0qNZvYK3W+jq/hlVFrCanuJgQqGVhO5h
7oxLsimEyXZMLyJW7KGrGMLrlOBM1ion1s6p7oJnhzv51T/rtOjehgL+CnllDy/1
vGMEABlPkRvoxtgpCKf8muTcwoZT97rxMcVeclyn+MeA3KYqkS6bVYtpd/GByF6B
ppKzfwgApMmXxX9laC3LptfESIrVvOe7RC163NUiH8O2L5e+KFYwiez3xVTUZ3SV
1zKvAU3gWPWaymEjvW7yD0ENnijYJgJ21Mx46pXsNvht+9MLlntYO9QtYnFideeF
Zrg2e/TlAgMBAAECggEAKbCDvx6/r+EfA3JLV2/KWXsADlmxa4d/pkhMiyDaOA8i
ucIAxmdOuB1vGDhcoREFtqIDt7fw3BJXQyD2/sPPANV7JeqcTAJ/A7A1NFtkYfZp
d1AdDA0vs5nuDcWIAzrDIGc1u2eOTmNUBw4nG5PcfT5WVrampDs/bKRB3ApCQUi4
Ha2O85yiDD9DtMFJ0sVpfVf+PfKs8C4Sveg2tPADitkv8mf1LD1VgPhck5nHc8Cb
PYbADWyLAbpGEVPf5Ytoqt8rR3DPGRgYKnKVB3xXHHrqal+DYnN4YzfzLbn1E8j6
svZYxJagEfwqAqdlIH8YXnWF2odVl/0t1nWTfJESVwKBgQDTY+Pi4GzFTIbH2r74
BmPbEKVzdRNMDRcnPoefUZZYfYyfinAcJx+SurGOeiYsxFKk583xOnUo4vB8Ny4s
9tSX+IMbpm//pRgpWALyoQ2XCEvmr08bSMdMNIlSfSrzb7ZWYGuOaYD6FWHgESIx
8QVs9oVXd8xYG6VA2dEbvVO3CwKBgQDSovXB0G8WMJqSD1yJQJY92K1QRQPq+P+f
H2pD2JI/Qt9nzK9tQz70OL/Opdbc7klAc+Fxe4t+nG5X8CO+LetZiC+GXc6jXu0Z
zd2D8CvpnJuLOlwXuj1ZAYZRCRPZt7QWPx66rPLG+a31TYhV880BrWfVSlM9GaCe
ZbZ7pAy5zwKBgQCBFbqPTKSfRr/3Quji5GQN3fhqrM9/3W6KFHEVJOk+WEyLECqt
xmZNX5wVeRnetHQTJ0pnSF9K/GoGMFwOMRxZQPQu6k/yOIqslIYYS6jU1cgjsmII
M0Ugj+RbbvvPdCRQUsjbSinsjSobfdr3zEqX42JBq3IpS5h6JYi/+7knSQKBgQDJ
5qBQ5h0FFROke4sEmN8qax49udm2inRjrzYp0Fbmwo/oLB/fSLz4VcktKHObE+Et
IaZwI68jboiqTCnGqrdT+6EqV5KxsFTvDhCN3lIJPC8wNoF90Ovq+/YmiN/5Kgg5
5sjqwMp5hWyEnIvZb7cLK49CuFqvT3hRPEe9nhMAqwKBgQCdnQV55B7Q/WI2lIGV
KYpgi8qozbg/8/LCbxng1u1v0WPmSVzoFLK/sP4uVwJP7Gia8ws++438/t5e3Jp4
JkMbbYugqXCdjIjsfYRieP0CHVxWsmPVfaU/3Z6zIqiRZqZE1IRsNDRqt9Nor8Vj
9Pwb/NeA1u0FvurZb83yeBClyA==
-----END PRIVATE KEY-----`;
};

/**
 * Limpa o cache de certificados (útil para testes ou recarregamento)
 */
export const clearCertificateCache = (): void => {
  certificateCache = {};
  console.log("🧹 Cache de certificados limpo");
};

/**
 * Obtém informações do certificado baseadas na configuração
 */
export const getCertificateInfo = () => {
  const config = getCertificateConfig();

  return {
    issuer: "QZ Tray Demo Certificate",
    subject: "QZ Tray Demo",
    organization: "QZ Industries, LLC",
    organizationalUnit: "QZ Tray Demo",
    country: "US",
    state: "New York",
    locality: "Buffalo",
    email: "support@qz.io",
    validFrom: "QZ Tray Generated",
    validTo: "QZ Tray Generated",
    usage: "QZ Tray Client Authentication",
    keySize: 2048,
    algorithm: "RSA with SHA-256",
    fingerprint: "QZ Tray Demo Certificate",
    source: "QZ Tray Generated Files",
    autoTrust: config.autoTrust,
    suppressDialog: config.suppressDialog,
  };
};
